import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchGenerationAttempts,
  fetchGenerationAttempt,
  fetchTemplateNames,
  retryGeneration,
} from "./generationApi";

const POLL_MS = 4000;

export function useGenerationAttempts() {
  const [attempts, setAttempts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");
  const [template, setTemplate] = useState("");
  const pollRef = useRef(null);

  const load = useCallback(
    async ({ silent } = {}) => {
      if (!silent) setLoading(true);
      try {
        const data = await fetchGenerationAttempts({ query, status, template });
        setAttempts(data);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [query, status, template],
  );

  useEffect(() => {
    const handle = setTimeout(load, 250);
    return () => clearTimeout(handle);
  }, [load]);

  // Poll quietly while anything is still in flight, so retries and
  // in-progress generations resolve on screen without a manual refresh.
  useEffect(() => {
    const hasInFlight = attempts.some((a) => a.status === "RUNNING" || a.status === "QUEUED");
    if (hasInFlight && !pollRef.current) {
      pollRef.current = setInterval(() => load({ silent: true }), POLL_MS);
    }
    if (!hasInFlight && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [attempts, load]);

  useEffect(() => {
    fetchTemplateNames().then(setTemplates);
  }, []);

  return {
    attempts,
    templates,
    loading,
    query,
    setQuery,
    status,
    setStatus,
    template,
    setTemplate,
    refresh: load,
  };
}

export function useGenerationAttempt(attemptId) {
  const [attempt, setAttempt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(
    async ({ silent } = {}) => {
      if (!attemptId) return;
      if (!silent) setLoading(true);
      try {
        const data = await fetchGenerationAttempt(attemptId);
        setAttempt(data);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [attemptId],
  );

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const inFlight = attempt?.status === "RUNNING" || attempt?.status === "QUEUED";
    if (inFlight && !pollRef.current) {
      pollRef.current = setInterval(() => load({ silent: true }), POLL_MS);
    }
    if (!inFlight && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [attempt, load]);

  const retry = useCallback(async () => {
    if (!attemptId) return;
    setRetrying(true);
    try {
      const newAttempt = await retryGeneration(attemptId);
      setAttempt(newAttempt);
      return newAttempt;
    } finally {
      setRetrying(false);
    }
  }, [attemptId]);

  return { attempt, loading, retrying, retry, refresh: load };
}